<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { isIOS } from "common/utils/browser";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { validateFile } from "common/utils/file";
import ScrollToElement from "common/utils/ui/scroll-to-element";
import CanvaButton from "components/jiffy/canva-button/CanvaButton.vue";

const props = defineProps({
  replica: {
    type: Boolean,
    default: false
  }
});

const store = useStore();
const canvaEnabled = window.jiffy_made_flips?.canva_enabled;
const isOrderLoading = computed(() => store.getters["loading/currentOrder"]);
const isSingleProductPage = computed(() => store.getters["product/isSingleProductPage"]);
const extensions = ["png", "gif", "jpeg", "jpg", "svg", "svg+xml"];
const accept = extensions.map(extension => `image/${extension}`);
const artwork = computed(() => store.getters["artwork/artwork"]);
const loading = computed(() => store.getters["loading/uploadArtwork"]);

const handleFileChange = e => {
  const file = e.target.files[0];
  const extension = `(${extensions.join("|")})`;

  if (validateFile(file, { extension, accept })) {
    store.dispatch("artwork/upload", file);

    // skip ios smart banner before scrolling
    isIOS() && window.scrollY === 0 && window.scrollTo({ top: 50 });

    // let browser finish all jobs before scrolling
    setTimeout(() => {
      ScrollToElement.scrollToElementBySelector("#pod-build-order-anchor");
    }, 0);
  } else {
    store.commit("setCurrentPage", "welcome");
    store.commit("errors/addNewError", {
      title: "Error",
      error: "You can't upload files of this type"
    });
  }
};
</script>

<template>
  <div v-if="!artwork && !loading" class="artwork-upload">
    <div
      class="artwork-upload__wrapper"
      :class="{
        'artwork-upload--loading': isOrderLoading,
        'artwork-upload--hidden': isSingleProductPage
      }"
    >
      <div class="artwork-upload__tap-wrapper">
        <UseSvg class="artwork-upload__icon" icon="upload-cloud" type="print" />
        <label class="artwork-upload__label" for="field-artwork-upload">Start Image Upload</label>
        <input
          id="field-artwork-upload"
          accept="image/*"
          class="artwork-upload__input"
          type="file"
          @change="handleFileChange"
        />
      </div>
    </div>

    <div v-if="canvaEnabled" class="artwork-upload__canva-section">
      <span class="artwork-upload__divider">or</span>
      <div class="artwork-upload__canva-button-wrap">
        <CanvaButton
          :file-landing="'#field-artwork-upload'"
          :namespace="'made'"
          :replica="props.replica"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.artwork-upload {
  $this: &;

  width: 320px;
  margin: 20px auto;

  @include with-desktop {
    width: 390px;
  }

  &__wrapper {
    border: 8px solid $pod-upload-border;
    border-radius: 12px;
    height: 64px;
    background: $pod-upload-border;
    box-shadow: 0 0 12px $pod-upload-bg;
    transition:
      box-shadow 0.3s,
      background-color 0.3s,
      border-color 0.3s;

    @include with-desktop {
      margin: 0;
      height: 84px;
      border-radius: 24px;
      box-shadow: 0 0 4px $pod-gray-modern-900;
    }

    &:focus,
    &:hover {
      box-shadow: 0 0 12px 3px $pod-upload-bg;

      @include with-desktop {
        box-shadow: 0 0 4px 2px $pod-upload-bg;
      }
    }

    &--loading {
      #{$this}__tap-wrapper {
        border-color: $pod-gray-modern-400;
        background-color: $pod-gray-modern-400;
        box-shadow: none;
        pointer-events: none;
      }
    }

    &--hidden {
      @include with-desktop {
        display: none;
      }
    }
  }

  &__tap-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    padding: 4px 20px;
    background-color: $pod-upload-bg;
    color: $white;
    text-align: center;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border-radius: 8px;

    @include with-desktop {
      border-radius: 20px;
    }
  }

  &__icon {
    width: 35px;
    height: 35px;
  }

  &__label {
    display: block;
    margin-bottom: 4px;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.2;
    text-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);

    @include with-desktop {
      font-size: 22px;
    }
  }

  &__text {
    font-size: 11px;
    font-weight: 600;
  }

  &__input {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
  }

  &__divider {
    display: block;
    margin: 0 auto 12px;
    color: $pod-gray-modern-600;
    font-size: 17px;
    font-weight: 600;
    text-align: center;

    @include with-desktop {
      color: $white;
    }
  }

  &__canva-section {
    margin-top: 16px;
  }

  &__canva-button-wrap {
    width: 236px;
    margin: auto;
  }
}
</style>
